import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Database } from "@/integrations/supabase/types";

type AppRole = Database["public"]["Enums"]["app_role"];

interface AuthContextType {
  session: Session | null;
  user: User | null;
  roles: AppRole[];
  profile: { full_name: string; avatar_url: string | null; phone: string | null; email?: string | null } | null;
  loading: boolean;
  signOut: () => Promise<void>;
  hasRole: (role: AppRole) => boolean;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [profile, setProfile] = useState<AuthContextType["profile"]>(null);
  const [loading, setLoading] = useState(true);
  const initialized = useRef(false);
  const fetchedUserId = useRef<string | null>(null);

  const fetchUserData = async (userId: string, force = false) => {
    if (!force && fetchedUserId.current === userId) return;
    fetchedUserId.current = userId;
    const [rolesRes, profileRes] = await Promise.all([
      supabase.from("user_roles").select("role").eq("user_id", userId),
      supabase.from("profiles").select("full_name, avatar_url, phone, email").eq("user_id", userId).single(),
    ]);
    if (rolesRes.data) setRoles(rolesRes.data.map((r) => r.role));
    if (profileRes.data) setProfile(profileRes.data);
  };

  useEffect(() => {
    const sessionPromise = supabase.auth.getSession();
    const timeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("getSession timeout")), 8000)
    );

    Promise.race([sessionPromise, timeout])
      .then(async (result) => {
        const sess = (result as any)?.data?.session ?? null;
        setSession(sess);
        setUser(sess?.user ?? null);
        if (sess?.user) {
          await fetchUserData(sess.user.id);
        }
      })
      .catch((err) => {
        console.error("[Auth] getSession failed:", err);
        setSession(null);
        setUser(null);
      })
      .finally(() => {
        setLoading(false);
        initialized.current = true;
      });

    const PUBLIC_PATHS = ["/login", "/reset-password"];

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === "TOKEN_REFRESHED" && !session) {
          setSession(null);
          setUser(null);
          setRoles([]);
          setProfile(null);
          fetchedUserId.current = null;
          if (!PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p))) {
            toast.error("Sua sessão expirou. Faça login novamente.");
            window.location.href = "/login";
          }
          return;
        }

        if (event === "SIGNED_OUT") {
          setSession(null);
          setUser(null);
          setRoles([]);
          setProfile(null);
          fetchedUserId.current = null;
          if (initialized.current) {
            setLoading(false);
          }
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          const uid = session.user.id;
          // Never await Supabase calls inside the auth callback (deadlock risk).
          setTimeout(() => {
            fetchUserData(uid).catch((err) => {
              console.warn("[Auth] fetchUserData failed:", err);
              setTimeout(() => {
                fetchUserData(uid, true).catch(() => {
                  toast.error("Erro ao carregar dados do usuário. Recarregue a página.");
                });
              }, 2000);
            });
          }, 0);
        } else {
          setRoles([]);
          setProfile(null);
          fetchedUserId.current = null;
        }
        if (initialized.current) {
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const refreshProfile = async () => {
    if (user?.id) {
      await fetchUserData(user.id, true);
    }
  };

  const hasRole = (role: AppRole) => roles.includes(role);

  return (
    <AuthContext.Provider value={{ session, user, roles, profile, loading, signOut, hasRole, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};
